import React, { useContext, useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    Col,
    ConfigProvider,
    Form,
    Input,
    message,
    Modal,
    Row,
    Select,
    Switch,
    Tag,
    Upload
} from "antd";
import locale from "antd/es/locale/es_ES";
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, TrophyOutlined } from "@ant-design/icons";

import { AuthContext } from "../../../config/AuthContext";
import BbddColors from "../../../styles/BbddColors";
import { GetAules } from "../../../dao/GetAules";
import { PostPractica } from "../../../dao/bbdd/PostPractica";
import { GetAulesIdConfiguracio } from "../../../dao/GetAulesIdConfiguracio";
import { GetAulaRaConfig } from "../../../dao/GetAulaRaConfig";
import { GetAulaDatabases, AulaDatabase } from "../../../dao/bbdd/AulaDatabasesApi";
import { DatabaseOutlined, RobotOutlined } from "@ant-design/icons";

interface Props {
    element: any;
    visible: boolean;
    onClose: (updated: boolean) => void;
}

const { TextArea } = Input;

const ConfigActivitatModal: React.FC<Props> = (props: Props) => {
    const [form] = Form.useForm();
    const { userId } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [aules, setAules] = useState<any[]>([]);
    const [rasAula, setRasAula] = useState<any[]>([]);
    const [aulaConfig, setAulaConfig] = useState<any[]>([]);
    
    const [previewAvatar, setPreviewAvatar] = useState<string | null>(null);
    // Bases de dades d'aula disponibles per fixar-ne una per defecte a l'activitat.
    const [aulaDatabases, setAulaDatabases] = useState<AulaDatabase[]>([]);

    useEffect(() => {
        const loadData = async () => {
            if (props.visible) {
                setAules(await GetAules(userId + ""));

                const ele = props.element;
                if (!ele || ele.id === 0) {
                    form.resetFields();
                    form.setFieldsValue({
                        id: 0,
                        nom: "",
                        estat: 0,
                        id_aula: undefined,
                        is_examen: false,
                        use_ia: false,
                        context: "",
                        id_aula_database: undefined,
                        tipus: 0,
                        ras: []
                    });
                    setPreviewAvatar(null);
                    setRasAula([]);
                    setAulaConfig([]);
                    setAulaDatabases([]);
                    return;
                }

                const isExamInitial = ele.is_examen === 1 || ele.is_examen === true || ele.isExamen === 1 || ele.isExamen === true;

                // Context IA: si no hi ha context o és "no context", el toggle queda desactivat.
                const rawContext = ele.context || "";
                const isNoContext = !rawContext || rawContext.trim().toLowerCase() === "no context";

                form.setFieldsValue({
                    id: ele.id,
                    nom: ele.nom,
                    estat: ele.estat,
                    id_aula: ele.id_aula || ele.idAula,
                    is_examen: isExamInitial,
                    use_ia: !isNoContext,
                    context: isNoContext ? "" : rawContext,
                    id_aula_database: ele.id_aula_database ?? ele.idAulaDatabase ?? undefined,
                    tipus: ele.tipus_aula || 0,
                    ras: (ele.ras || []).map((ra: any) => ({
                        id: ra.id,
                        percentatge: ra.percentatge || 0,
                        instrument: ra.instrument
                    }))
                });

                if (ele.id_aula) {
                    try {
                        // Carreguem les bases de dades d'aula disponibles per a la BD per defecte
                        try {
                            const dbs = await GetAulaDatabases(ele.id_aula);
                            setAulaDatabases(Array.isArray(dbs) ? dbs : []);
                        } catch { setAulaDatabases([]); }

                        const res = await GetAulesIdConfiguracio(ele.id_aula, 1);
                        if (res && Array.isArray(res)) {
                            const allRas = res.flatMap((item: any) => item.list || []).map((ra: any) => ({
                                id: ra.id,
                                nom: ra.num,
                                descripcio: ra.desc
                            }));
                            setRasAula(allRas);
                        } else {
                            setRasAula([]);
                        }
                        const resConfig = await GetAulaRaConfig(ele.id_aula);
                        setAulaConfig(resConfig || []);
                    } catch (e) {
                        console.error("Error loading aula RAs in BBDD:", e);
                    }
                }

                if (ele.avatar) {
                    let av = ele.avatar;
                    if (av.startsWith("base64:")) {
                        const parts = av.split(":");
                        av = parts[parts.length - 1];
                    }
                    setPreviewAvatar(`data:image/png;base64,${av}`);
                } else {
                    setPreviewAvatar(null);
                }
            }
        };
        loadData();
    }, [props.element, props.visible]);

    const handleAulaChange = async (aulaId: any) => {
        if (!aulaId) {
            setRasAula([]);
            setAulaConfig([]);
            setAulaDatabases([]);
            form.setFieldsValue({ ras: [], id_aula_database: undefined });
            return;
        }
        try {
            // Bases de dades d'aula disponibles per a aquesta aula (BD per defecte de l'activitat)
            try {
                const dbs = await GetAulaDatabases(aulaId);
                setAulaDatabases(Array.isArray(dbs) ? dbs : []);
            } catch { setAulaDatabases([]); }
            // En canviar d'aula, la BD per defecte seleccionada pot deixar de ser vàlida.
            form.setFieldsValue({ id_aula_database: undefined });

            const res = await GetAulesIdConfiguracio(aulaId, 1);
            if (res && Array.isArray(res)) {
                const allRas = res.flatMap((item: any) => item.list || []).map((ra: any) => ({
                    id: ra.id,
                    nom: ra.num,
                    descripcio: ra.desc
                }));
                setRasAula(allRas);
            } else {
                setRasAula([]);
            }
            const resConfig = await GetAulaRaConfig(aulaId);
            setAulaConfig(resConfig || []);
            // Reiniciar RAs seleccionados porque cambia el aula
            form.setFieldsValue({ ras: [] });
        } catch (e) {
            console.error("Error fetching aula RAs:", e);
        }
    };

    const handleCancel = () => {
        props.onClose(false);
        form.resetFields();
        setPreviewAvatar(null);
    };

    async function submitData(values: any) {
        try {
            setLoading(true);
            const avatarBase64 = previewAvatar ? previewAvatar.split(",")[1] : null;

            // Context IA: si el toggle està desactivat, es desa "no context" (igual que a jutge).
            const finalContext = values.use_ia
                ? (values.context && values.context.trim() ? values.context.trim() : "no context")
                : "no context";

            const isExamVal = values.is_examen ? 1 : 0;
            const payload = {
                ...values,
                id: props.element?.id || 0,
                is_examen: isExamVal,
                isExamen: isExamVal,
                context: finalContext,
                // La imatge ja no es gestiona aquí; es manté l'avatar.
                avatar: avatarBase64,
                id_aula_database: values.id_aula_database ?? null,
                idAulaDatabase: values.id_aula_database ?? null,
                tipus_aula: values.tipus,
                idAula: values.id_aula,
                id_aula: values.id_aula,
                ras: (values.ras || []).map((ra: any) => ({
                    id: ra.id,
                    percentatge: ra.percentatge || 0,
                    instrument: ra.instrument || ""
                }))
            };

            await PostPractica(payload);

            message.success("Activitat desada correctament");
            props.onClose(true);
        } catch (err) {
            message.error("Error desant l'activitat");
        } finally {
            setLoading(false);
        }
    }

    const uploadAvatarProps = {
        beforeUpload: (file: File) => {
            const reader = new FileReader();
            reader.onload = (e: any) => setPreviewAvatar(e.target.result);
            reader.readAsDataURL(file);
            return false;
        },
    };

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={
                    props.element?.id && props.element.id !== 0
                        ? `Editar activitat: ${props.element.nom}`
                        : "Nova activitat"
                }
                open={props.visible}
                onCancel={handleCancel}
                footer={[]}
                width={800}
            >
                <hr />
                <Form
                    form={form}
                    onFinish={submitData}
                    layout="vertical"
                    initialValues={{ id: 0, tipus: 0, estat: 1 }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="ID" name="id" hidden={!props.element?.id}>
                                <Input disabled />
                            </Form.Item>

                            <Form.Item
                                label="Nom"
                                name="nom"
                                rules={[{ required: true, message: "Introdueix el nom" }]}
                            >
                                <Input placeholder="Nom de l'activitat" />
                            </Form.Item>

                            <Form.Item
                                label="Tipus"
                                name="tipus"
                                rules={[{ required: true, message: "Selecciona el tipus" }]}
                            >
                                <Select placeholder="Selecciona el tipus" disabled={true}>
                                    <Select.Option value={0}>Activitat</Select.Option>
                                    <Select.Option value={1}>Pràctica</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Estat"
                                name="estat"
                                rules={[{ required: true, message: "Selecciona un estat" }]}
                            >
                                <Select placeholder="Selecciona l'estat">
                                    <Select.Option value={0}>
                                        <Tag color={BbddColors.colors.color1}>No visible</Tag>
                                    </Select.Option>
                                    <Select.Option value={2}>
                                        <Tag color={BbddColors.colors.color5}>Tancat</Tag>
                                    </Select.Option>
                                    <Select.Option value={3}>
                                        <Tag color={BbddColors.colors.color3}>Actiu</Tag>
                                    </Select.Option>
                                    <Select.Option value={4}>
                                        <Tag color={BbddColors.colors.color4}>Revisió</Tag>
                                    </Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Aula"
                                name="id_aula"
                                rules={[{ required: true, message: "Selecciona una aula" }]}
                            >
                                <Select
                                    placeholder="Selecciona una aula"
                                    onChange={handleAulaChange}
                                >
                                    {aules.map((aula) => (
                                        <Select.Option key={aula.id} value={aula.id}>
                                            {aula.nom}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 14px',
                                background: '#fafafa',
                                borderRadius: 8,
                                border: '1px solid #e8e8e8',
                                marginBottom: 16
                            }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                                        <TrophyOutlined style={{ color: '#faad14', marginRight: 6 }} /> Mode Examen
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                        Apareixerà a la pestanya d'Exàmens en lloc d'Activitats
                                    </div>
                                </div>
                                <Form.Item name="is_examen" valuePropName="checked" noStyle>
                                    <Switch />
                                </Form.Item>
                            </div>
                        </Col>

                        <Col span={12}>
                            {/* Base de dades per defecte: tots els exercicis de l'activitat
                                l'hereten tret que n'especifiquin una de pròpia. */}
                            <Form.Item
                                label={<span><DatabaseOutlined style={{ marginRight: 6, color: '#1890ff' }} />Base de dades per defecte</span>}
                                name="id_aula_database"
                                tooltip="Els exercicis d'aquesta activitat faran servir aquesta base de dades si no en tenen una de pròpia."
                            >
                                <Select
                                    allowClear
                                    placeholder="Cap (cada exercici la defineix)"
                                    notFoundContent={aulaDatabases.length === 0 ? "Aquesta aula no té bases de dades" : undefined}
                                >
                                    {aulaDatabases.map((db) => (
                                        <Select.Option key={db.id} value={db.id}>
                                            {db.nom} <span style={{ color: '#999' }}>({db.schema_name})</span>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/* Toggle de context IA (com a jutge): si està desactivat es desa "no context". */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 14px',
                                background: '#fff0f6',
                                borderRadius: 8,
                                border: '1px solid #ffd6e7',
                                marginBottom: 16
                            }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                                        <RobotOutlined style={{ color: '#eb2f96', marginRight: 6 }} /> Habilitar IA amb Context
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                        Defineix instruccions i context per la IA. Si està desactivat, es desa "no context".
                                    </div>
                                </div>
                                <Form.Item name="use_ia" valuePropName="checked" noStyle>
                                    <Switch />
                                </Form.Item>
                            </div>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) => prev.use_ia !== curr.use_ia}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('use_ia') ? (
                                        <Form.Item label="Context" name="context">
                                            <TextArea
                                                placeholder="Descripció o context de l'activitat per la IA"
                                                rows={6}
                                            />
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>

                            <Form.Item label="Avatar">
                                <Upload {...uploadAvatarProps} showUploadList={false}>
                                    <Button icon={<UploadOutlined />}>Pujar avatar</Button>
                                </Upload>
                                {previewAvatar && (
                                    <div style={{ marginTop: 12, textAlign: "center" }}>
                                        <img
                                            src={previewAvatar}
                                            alt="Avatar Preview"
                                            style={{
                                                width: 120,
                                                height: 120,
                                                objectFit: "cover",
                                                borderRadius: "50%",
                                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                            }}
                                        />
                                    </div>
                                )}
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Resultats d'Aprenentatge (RAs) */}
                    <div style={{
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 10,
                        padding: 16,
                        marginTop: 16,
                        marginBottom: 16
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#434343', marginBottom: 12 }}>
                            Resultats d'Aprenentatge (RAs) Associats
                        </div>

                        <Form.List name="ras">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row
                                            key={key}
                                            gutter={8}
                                            align="middle"
                                            style={{
                                                marginBottom: 8,
                                                background: '#fff',
                                                border: '1px solid #e8e8e8',
                                                padding: '10px 12px',
                                                borderRadius: 6
                                            }}
                                        >
                                            <Col span={10}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'id']}
                                                    label="RA"
                                                    rules={[{ required: true, message: 'Falta RA' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select
                                                        placeholder="Selecciona RA"
                                                        onChange={() => {
                                                            const currentRas = form.getFieldValue('ras');
                                                            if (currentRas && currentRas[name]) {
                                                                currentRas[name].instrument = undefined;
                                                                form.setFieldsValue({ ras: currentRas });
                                                            }
                                                        }}
                                                    >
                                                        {rasAula.map((ra: any) => (
                                                            <Select.Option key={ra.id} value={ra.id}>
                                                                {ra.nom} - {ra.descripcio}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>

                                            <Col span={12}>
                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prev, curr) => {
                                                        return prev.ras?.[name]?.id !== curr.ras?.[name]?.id || prev.id_aula !== curr.id_aula;
                                                    }}
                                                >
                                                    {({ getFieldValue }) => {
                                                        const ras = getFieldValue('ras') || [];
                                                        const currentRaId = ras[name]?.id;
                                                        const currentRaObj = rasAula.find((r: any) => r.id === currentRaId);
                                                        const raName = currentRaObj ? currentRaObj.nom : '';

                                                        const instruments = (currentRaObj && aulaConfig)
                                                            ? aulaConfig.filter((c: any) =>
                                                                c.nom_ra === raName ||
                                                                c.nom_ra === `RA${raName}` ||
                                                                (c.nom_ra && c.nom_ra.replace(/^RA/, '') === raName)
                                                            ).map((c: any) => c.instrument)
                                                            : [];

                                                        const uniqueInstruments = Array.from(new Set(instruments));

                                                        return (
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'instrument']}
                                                                label="Instrument"
                                                                rules={[{ required: true, message: 'Falta Instrument' }]}
                                                                style={{ marginBottom: 0 }}
                                                            >
                                                                <Select placeholder="Selecciona Instrument" disabled={!currentRaId}>
                                                                    {uniqueInstruments.map((inst: any) => (
                                                                        <Select.Option key={inst} value={inst}>
                                                                            {inst}
                                                                        </Select.Option>
                                                                    ))}
                                                                </Select>
                                                            </Form.Item>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </Col>
                                            <Col span={2} style={{ textAlign: 'center' }}>
                                                <Form.Item name={[name, 'percentatge']} noStyle hidden>
                                                    <Input />
                                                </Form.Item>
                                                <MinusCircleOutlined
                                                    onClick={() => remove(name)}
                                                    style={{ color: '#ff4d4f', fontSize: 16, cursor: 'pointer' }}
                                                />
                                            </Col>
                                        </Row>
                                    ))}
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                        icon={<PlusOutlined />}
                                        style={{ marginTop: fields.length > 0 ? 8 : 0, borderRadius: 6 }}
                                    >
                                        Afegir RA
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </div>

                    <div style={{ textAlign: "right", marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ marginRight: 10 }}>
                            Cancel·lar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            style={{ backgroundColor: BbddColors.colors.color6, border: "none" }}
                        >
                            Desa
                        </Button>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default ConfigActivitatModal;
